import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Check,
  Globe,
  GlobeLock,
  Link,
  QrCode,
  Share2,
  Loader2,
  ExternalLink,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { useForm } from '../../hooks/useForms';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../Button';
import { Reveal } from '../Reveal';
import { slugify, validateSlug, checkSlugAvailability } from '../../lib/slugUtils';

interface FormShareProps {
  embedded?: boolean;
}

export const FormShare: React.FC<FormShareProps> = ({ embedded = false }) => {
  const { id: formId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { form, loading, updateForm } = useForm(formId);
  const { user } = useAuth();

  const [copied, setCopied] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Slug state
  const [slugInput, setSlugInput] = useState('');
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'invalid'>('idle');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [isSavingSlug, setIsSavingSlug] = useState(false);
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Initialize slug input when form loads
  useEffect(() => {
    if (form?.slug) {
      setSlugInput(form.slug);
      setSlugStatus('available');
    }
  }, [form?.slug]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
    };
  }, []);

  const formUrl = `${window.location.origin}/f/${formId}`;
  const hasVanityUrl = user?.username && form?.slug;
  const vanityUrl = hasVanityUrl
    ? `${window.location.origin}/@${user.username}/${form.slug}`
    : null;
  const displayUrl = vanityUrl || formUrl;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // QR Code da URL pública. Import lazy: a lib (~30KB) não entra no TTI da rota.
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    import('qrcode')
      .then((QRCode) =>
        // 512px: nítido para baixar/imprimir; o <img> exibe reduzido em 192px
        QRCode.toDataURL(displayUrl, { width: 512, margin: 2, errorCorrectionLevel: 'M' })
      )
      .then((url) => {
        if (active) setQrDataUrl(url);
      })
      .catch((err) => {
        console.error('[FormShare] Falha ao gerar QR Code:', err);
      });
    return () => {
      active = false;
    };
  }, [displayUrl]);

  const handleDownloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `qrcode-${form?.slug || formId || 'formulario'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const debouncedCheckSlug = (slug: string) => {
    if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
    checkTimeoutRef.current = setTimeout(async () => {
      if (!user) return;
      const { available } = await checkSlugAvailability(user.id, slug, formId);
      setSlugStatus(available ? 'available' : 'unavailable');
      setSlugError(available ? null : 'Este slug já está em uso');
    }, 500);
  };

  const handleSlugChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlugInput(sanitized);

    if (!sanitized) {
      setSlugStatus('idle');
      setSlugError(null);
      return;
    }

    // Skip check if slug unchanged from saved value
    if (sanitized === form?.slug) {
      setSlugStatus('available');
      setSlugError(null);
      return;
    }

    const validationError = validateSlug(sanitized);
    if (validationError) {
      setSlugStatus('invalid');
      setSlugError(validationError);
      return;
    }

    setSlugStatus('checking');
    setSlugError(null);
    debouncedCheckSlug(sanitized);
  };

  const handleSaveSlug = async () => {
    setIsSavingSlug(true);
    const slug = slugInput.trim() || null;
    await updateForm({ slug });
    setIsSavingSlug(false);
  };

  const suggestedSlug = form?.title ? slugify(form.title) : '';

  const handleUseSuggestion = () => {
    setSlugInput(suggestedSlug);
    const validationError = validateSlug(suggestedSlug);
    if (validationError) {
      setSlugStatus('invalid');
      setSlugError(validationError);
      return;
    }
    setSlugStatus('checking');
    setSlugError(null);
    debouncedCheckSlug(suggestedSlug);
  };

  const handleTogglePublish = async () => {
    setIsPublishing(true);
    await updateForm({ is_published: !form?.is_published });
    setIsPublishing(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header (hidden when embedded in editor) */}
      {!embedded && (
        <Reveal variant="fade-up">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div>
              <h1 className="text-2xl font-bold text-white">{form?.title}</h1>
              <p className="text-neutral-400">Compartilhar formulário</p>
            </div>
          </div>
        </Reveal>
      )}

      {/* Status do formulário */}
      <Reveal variant="fade-up" delay={100}>
        <div className="liquid-glass rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {form?.is_published ? (
                <>
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Formulário publicado</h3>
                    <p className="text-neutral-400 text-sm">Qualquer pessoa com o link pode responder</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-xl bg-neutral-700 flex items-center justify-center">
                    <GlobeLock className="w-6 h-6 text-neutral-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Formulário em rascunho</h3>
                    <p className="text-neutral-400 text-sm">Publique para começar a receber respostas</p>
                  </div>
                </>
              )}
            </div>

            <Button
              variant={form?.is_published ? 'outline' : 'primary'}
              onClick={handleTogglePublish}
              disabled={isPublishing}
            >
              {isPublishing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : form?.is_published ? (
                'Despublicar'
              ) : (
                'Publicar'
              )}
            </Button>
          </div>
        </div>
      </Reveal>

      {/* Link Personalizado */}
      <Reveal variant="fade-up" delay={200}>
        <div className="liquid-glass rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-primary-400" />
            <h3 className="text-lg font-semibold text-white">Link Personalizado</h3>
          </div>

          {!user?.username ? (
            <div className="flex items-center gap-2 text-amber-400 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>
                Defina seu @username nas{' '}
                <RouterLink to="/dashboard/profile" className="underline hover:text-amber-300">
                  configurações de perfil
                </RouterLink>{' '}
                para usar links personalizados.
              </span>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-sm text-neutral-400 mb-2 truncate">
                  {window.location.host}/@{user.username}/
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={slugInput}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="meu-formulario"
                      className="w-full bg-neutral-900/50 border border-neutral-700 rounded-xl px-4 py-3 text-white placeholder-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                    {slugStatus === 'checking' && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                      </span>
                    )}
                    {slugStatus === 'available' && slugInput && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Check className="w-4 h-4 text-green-400" />
                      </span>
                    )}
                    {(slugStatus === 'unavailable' || slugStatus === 'invalid') && slugInput && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2">
                        <AlertCircle className="w-4 h-4 text-red-400" />
                      </span>
                    )}
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleSaveSlug}
                    disabled={isSavingSlug || (slugStatus !== 'available' && slugStatus !== 'idle') || (slugInput === (form?.slug || ''))}
                  >
                    {isSavingSlug ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                </div>

                {slugError && slugInput && (
                  <p className="mt-2 text-sm text-red-400">{slugError}</p>
                )}
                {slugStatus === 'available' && slugInput && (
                  <p className="mt-2 text-sm text-green-400">Disponível</p>
                )}
              </div>

              {suggestedSlug && suggestedSlug !== slugInput && (
                <button
                  onClick={handleUseSuggestion}
                  className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <Sparkles className="w-3 h-3" />
                  Sugestão: {suggestedSlug}
                </button>
              )}
            </div>
          )}
        </div>
      </Reveal>

      {/* Link compartilhável */}
      <Reveal variant="fade-up" delay={300}>
        <div className="liquid-glass rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Link className="w-5 h-5 text-primary-400" />
            <h3 className="text-lg font-semibold text-white">Link do formulário</h3>
          </div>

          <div className="flex gap-3">
            <div className="flex-1 bg-neutral-900/50 border border-neutral-700 rounded-xl px-4 py-3 text-neutral-300 overflow-hidden">
              <span className="truncate block">{displayUrl}</span>
            </div>
            <Button variant="primary" onClick={handleCopy}>
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copiado!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar
                </>
              )}
            </Button>
          </div>

          {form?.is_published && (
            <a
              href={displayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir em nova aba
            </a>
          )}
        </div>
      </Reveal>

      {/* QR Code */}
      <Reveal variant="fade-up" delay={400}>
        <div className="liquid-glass rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <QrCode className="w-5 h-5 text-primary-400" />
            <h3 className="text-lg font-semibold text-white">QR Code</h3>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-48 h-48 bg-white rounded-2xl p-4 flex items-center justify-center">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt={`QR Code do formulário: ${displayUrl}`}
                  className="w-full h-full rounded-lg"
                />
              ) : (
                <div className="w-full h-full bg-neutral-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-20 h-20 text-neutral-300" />
                </div>
              )}
            </div>

            <div className="flex-1 text-center sm:text-left">
              <p className="text-neutral-400 mb-4">
                Escaneie o QR Code para acessar o formulário diretamente no celular.
              </p>
              <Button variant="outline" onClick={handleDownloadQr} disabled={!qrDataUrl}>
                <Download className="w-4 h-4 mr-2" />
                Baixar QR Code
              </Button>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Opções de compartilhamento */}
      <Reveal variant="fade-up" delay={500}>
        <div className="liquid-glass rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Share2 className="w-5 h-5 text-primary-400" />
            <h3 className="text-lg font-semibold text-white">Compartilhar via</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Preencha este formulário: ${displayUrl}`)}`, '_blank')}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-neutral-900/50 hover:bg-neutral-800 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <span className="text-sm text-neutral-300">WhatsApp</span>
            </button>

            <button
              onClick={() => window.open(`mailto:?subject=Formulário&body=${encodeURIComponent(`Preencha este formulário: ${displayUrl}`)}`, '_blank')}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-neutral-900/50 hover:bg-neutral-800 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-sm text-neutral-300">Email</span>
            </button>

            <button
              onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(displayUrl)}`, '_blank')}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-neutral-900/50 hover:bg-neutral-800 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </div>
              <span className="text-sm text-neutral-300">Telegram</span>
            </button>

            <button
              onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(displayUrl)}`, '_blank')}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-neutral-900/50 hover:bg-neutral-800 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </div>
              <span className="text-sm text-neutral-300">X (Twitter)</span>
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  );
};

// Missing Download icon from lucide-react
const Download: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);
